import React, { useMemo, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateUtc } from '../../utils/dateFormat';
import { StatCard, StatusChip, ActionButton, DataCard, Icons, BgArt } from './ui';
import Reveal, { RevealStagger } from '../Reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEUR = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtPct = (n) => `${(n * 100).toFixed(1).replace('.', ',')} %`;

const greeting = () => {
  const h = new Date().getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
};

const monthsBetween = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start); const e = new Date(end);
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
};

const Icon = {
  wallet:   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M21 12V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2v-2M16 12h5M16 12a2 2 0 010 4h5"/></svg>,
  percent:  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><line x1="19" y1="5" x2="5" y2="19" strokeLinecap="round"/><circle cx="7.5" cy="7.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>,
  bell:     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0a3 3 0 11-6 0"/></svg>,
  arrowOut: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>,
  sparkle:  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v3m0 12v3m9-9h-3M6 12H3m13.5-6.5L14 8M10 16l-2.5 2.5m9 0L14 16M10 8L7.5 5.5M12 8a4 4 0 100 8 4 4 0 000-8z"/></svg>,
  download: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/></svg>,
  doc:      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m-6-8h6M5 21h14a2 2 0 002-2V7l-5-5H7a2 2 0 00-2 2v15z"/></svg>,
  mail:     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
  bank:     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}><path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M4 11h16M5 11v10M19 11v10M9 11v10M15 11v10M3 7l9-4 9 4v4H3V7z"/></svg>,
  payoutOk: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 12.2l2.4 2.4 4.6-4.8" />
    </svg>
  ),
};

// Sparkline component (decorative)
const Sparkline = ({ width = 220, height = 60, color = '#C5A059', seed = 1 }) => {
  const points = useMemo(() => {
    const arr = []; let v = 30;
    for (let i = 0; i < 16; i++) {
      v += ((Math.sin(seed + i * 0.7) + 0.4) * 3) + 0.8;
      arr.push(v);
    }
    return arr;
  }, [seed]);
  const max = Math.max(...points); const min = Math.min(...points);
  const range = Math.max(1, max - min);
  const sx = width / (points.length - 1);
  const path = points.map((d, i) => `${i === 0 ? 'M' : 'L'} ${i * sx} ${height - ((d - min) / range) * (height - 8) - 4}`).join(' ');
  const area = path + ` L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={`spk-${seed}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#spk-${seed})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
};

// ============================================================
// Main component
// ============================================================
const PremiumOverview = ({ dashboard, user, setActiveTab, onCsvExport, onPdfExport, onReload }) => {
  const investments = dashboard.investments && dashboard.investments.length
    ? dashboard.investments
    : (dashboard.investment ? [dashboard.investment] : []);

  const totalPrincipal = investments.reduce((s, i) => s + (i.principal_eur || 0), 0);
  const totalAccrued = dashboard.total_accrued_interest || 0;
  const totalPaid = dashboard.total_confirmed_payouts || dashboard.total_paid_out || 0;
  const avgApr = investments.length ? investments.reduce((s, i) => s + (i.apr || 0), 0) / investments.length : 0;
  const next = dashboard.next_payout;
  const recentPayouts = (dashboard.recent_payouts || []).filter((p) => p.status === 'confirmed').slice(0, 3);
  const recentAccruals = (dashboard.recent_accruals || []).slice(0, 4);
  const multi = investments.length > 1;

  return (
    <div className="space-y-6 lg:space-y-8 vp-stagger" data-testid="premium-overview">
      {/* Hero — premium editorial header with gold rule */}
      <section className="relative vp-anim-rise" data-testid="overview-hero">
        {/* Subtle decorative line */}
        <div className="hidden sm:block absolute -top-2 left-0 w-12 h-px bg-gradient-to-r from-[#C5A059] to-transparent"></div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] font-medium mb-3">
          Ihr Portfolio · {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' })}
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] leading-[1.05] tracking-tight">
          {greeting()}, <span className="italic text-[#C5A059]">{user?.name?.split(' ')[0] || 'Investor'}</span>
        </h2>
        <p className="text-sm sm:text-base text-gray-500 mt-3 max-w-2xl">
          {multi
            ? <>Sie verwalten <strong className="text-[#0A0A0A] tabular-nums">{investments.length} aktive Investments</strong> mit einer Gesamteinlage von <strong className="text-[#0A0A0A] tabular-nums">{fmtEUR(totalPrincipal)}</strong>.</>
            : 'Schön, dass Sie da sind. Hier ist Ihr aktueller Überblick.'}
        </p>
      </section>

      {/* Advisor strip (private-banking touch) */}
      <div className="vp-anim-rise">
        <AdvisorStrip advisor={dashboard.advisor} />
      </div>

      {/* KPI strip — desktop grid, mobile scroll-snap carousel */}
      <section className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 vp-stagger vp-anim-rise">
        <StatCard label="Gesamteinlage" value={fmtEUR(totalPrincipal)}
          sub={multi ? `${investments.length} Verträge` : (investments[0]?.product_code || 'Basis')}
          icon={Icon.wallet} bgIcon={BgArt.vault} testid="kpi-investment" />
        <StatCard label="Rendite" value={fmtPct(avgApr)} sub={multi ? 'Ø über alle Verträge' : 'Fester Zinssatz'}
          icon={Icon.percent} bgIcon={BgArt.chart} variant="gold" testid="kpi-yield" />
        <StatCard label="Nächste Auszahlung"
          value={next ? fmtEUR(next.amount_eur) : '—'}
          sub={next ? `Fällig ${formatDateUtc(next.due_date)}` : 'Keine offen'}
          icon={Icon.bell} bgIcon={BgArt.calendar} testid="kpi-next-payout" />
        <StatCard label="Ausgezahlt" value={fmtEUR(totalPaid)} sub="Seit Vertragsbeginn"
          icon={Icon.arrowOut} bgIcon={BgArt.bank} variant="sky" testid="kpi-paid" />
        <StatCard label="Aufgelaufene Zinsen" value={fmtEUR(totalAccrued)} sub="Bis heute"
          icon={Icon.sparkle} bgIcon={BgArt.coins} variant="success" testid="kpi-accrued" />
      </section>

      {/* Mobile KPI carousel — scroll-snap */}
      <section className="sm:hidden -mx-4 px-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide" data-testid="kpi-carousel">
        <div className="flex gap-3 pb-2">
          {[
            { label: 'Gesamteinlage', value: fmtEUR(totalPrincipal), sub: multi ? `${investments.length} Verträge` : 'Basis', icon: Icon.wallet, bg: BgArt.vault, variant: 'default', tid: 'kpi-investment-m' },
            { label: 'Rendite', value: fmtPct(avgApr), sub: 'Fester Zinssatz', icon: Icon.percent, bg: BgArt.chart, variant: 'gold', tid: 'kpi-yield-m' },
            { label: 'Nächste Auszahlung', value: next ? fmtEUR(next.amount_eur) : '—', sub: next ? `Fällig ${formatDateUtc(next.due_date)}` : 'Keine offen', icon: Icon.bell, bg: BgArt.calendar, variant: 'default', tid: 'kpi-next-payout-m' },
            { label: 'Ausgezahlt', value: fmtEUR(totalPaid), sub: 'Seit Vertragsbeginn', icon: Icon.arrowOut, bg: BgArt.bank, variant: 'sky', tid: 'kpi-paid-m' },
            { label: 'Aufgelaufene Zinsen', value: fmtEUR(totalAccrued), sub: 'Bis heute', icon: Icon.sparkle, bg: BgArt.coins, variant: 'success', tid: 'kpi-accrued-m' },
          ].map((k) => (
            <div key={k.tid} className="snap-start flex-shrink-0 w-[78%]">
              <StatCard label={k.label} value={k.value} sub={k.sub} icon={k.icon} bgIcon={k.bg} variant={k.variant} testid={k.tid} />
            </div>
          ))}
        </div>
      </section>

      {/* Main: investment + right column */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Investments — wider */}
        <Reveal as="div" className="lg:col-span-2 space-y-4 lg:space-y-6" distance={32}>
          {/* Investment cards */}
          {investments.length === 0 ? (
            <DataCard><p className="text-sm text-gray-500 text-center py-8">Noch kein Investment aktiviert.</p></DataCard>
          ) : multi ? (
            <DataCard
              title={`Ihre ${investments.length} Investments`}
              action={
                <div className="flex items-center gap-2">
                  <ActionButton variant="secondary" size="xs" icon={Icon.download} onClick={onCsvExport} data-testid="export-csv-button">CSV</ActionButton>
                  <ActionButton variant="secondary" size="xs" icon={Icon.download} onClick={onPdfExport} data-testid="export-pdf-button">PDF</ActionButton>
                </div>
              }
              testid="investment-list-card"
            >
              <div className="space-y-3">
                {investments.map((inv, idx) => (
                  <InvestmentRow key={inv.id} inv={inv} index={idx + 1} onSaved={onReload} />
                ))}
              </div>
              <button
                onClick={() => setActiveTab('performance')}
                className="mt-4 text-sm text-[#C5A059] hover:underline inline-flex items-center gap-1"
                data-testid="investment-list-details-link"
              >
                Vollständige Entwicklung ansehen
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </DataCard>
          ) : (
            <InvestmentCard inv={investments[0]} onCsv={onCsvExport} onPdf={onPdfExport} onSaved={onReload} />
          )}

          {/* Lower data block — desktop only between investment and right column? -> below in lower section */}
        </Reveal>

        {/* Right column */}
        <Reveal as="div" className="space-y-4 lg:space-y-6" delay={120} distance={32}>
          {/* Next payout (dark, premium with texture) */}
          <div
            className="relative rounded-2xl text-white p-6 overflow-hidden ring-1 ring-white/5"
            style={{ background: 'radial-gradient(circle at 100% 0%, #1F1F1F 0%, #0A0A0A 60%)' }}
            data-testid="next-payout-card"
          >
            {/* Grain / dot pattern */}
            <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(#C5A059 1px, transparent 1px)', backgroundSize: '14px 14px' }}
            ></div>
            <svg className="absolute right-3 bottom-3 w-28 h-28 opacity-20" viewBox="0 0 100 100" fill="none" stroke="#C5A059" strokeWidth="1">
              <rect x="15" y="22" width="70" height="60" rx="6" />
              <line x1="15" y1="38" x2="85" y2="38" />
              <line x1="32" y1="22" x2="32" y2="14" strokeLinecap="round" />
              <line x1="68" y1="22" x2="68" y2="14" strokeLinecap="round" />
              {Array.from({ length: 12 }).map((_, i) => (
                <circle key={i} cx={28 + (i % 4) * 14} cy={50 + Math.floor(i / 4) * 11} r="1.5" fill="#C5A059" />
              ))}
            </svg>
            <p className="relative text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-3">Nächste Auszahlung</p>
            <p className="relative font-heading text-4xl text-[#C5A059] tabular-nums leading-none" data-testid="next-payout-amount">
              {next ? fmtEUR(next.amount_eur) : '—'}
            </p>
            <div className="relative w-12 h-px bg-[#C5A059]/40 my-4"></div>
            <p className="relative text-xs text-white/70 tabular-nums">
              {next ? `Fällig am ${formatDateUtc(next.due_date)}` : 'Aktuell keine offene Auszahlung'}
            </p>
            {next && (
              <span className="relative mt-3 inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-md border border-[#C5A059]/40 text-[#C5A059] uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-pulse"></span>
                Status: Fällig
              </span>
            )}
          </div>

          {/* Recent payouts */}
          <DataCard
            title="Letzte Auszahlungen"
            action={<button onClick={() => setActiveTab('payouts')} className="text-xs text-[#C5A059] hover:underline inline-flex items-center gap-1" data-testid="payouts-show-all">Alle anzeigen →</button>}
            testid="recent-payouts-card"
          >
            {recentPayouts.length === 0 ? (
              <p className="text-xs text-gray-500">Noch keine bestätigten Auszahlungen.</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {recentPayouts.map((p) => (
                  <li key={p.id} className="grid grid-cols-[40px_1fr_auto] items-center gap-3 py-3" data-testid={`recent-payout-${p.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 ring-1 ring-emerald-100/60">{Icon.payoutOk}</div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0A0A0A] tabular-nums">{fmtEUR(p.amount_eur)}</p>
                      <p className="text-[11px] text-gray-500 tabular-nums">{formatDateUtc(p.confirmed_at || p.due_date)}</p>
                    </div>
                    <StatusChip tone="confirmed">Bestätigt</StatusChip>
                  </li>
                ))}
              </ul>
            )}
          </DataCard>
        </Reveal>
      </section>

      {/* Lower section — cards reveal in cascade as they scroll into view */}
      <RevealStagger as="section" step={140} distance={36} className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <DataCard title="Zinsen & Auszahlungen" testid="card-zinsen-auszahlungen" bgIcon={BgArt.coins} bgIconColor="#15803d">
          <dl className="flex-1 flex flex-col justify-between gap-6 py-2">
            <DataLine label="Aufgelaufene Zinsen" value={fmtEUR(totalAccrued)} valueClass="text-emerald-700" sub="Bis heute" testid="zinsen-accrued" />
            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
            <DataLine label="Ausgezahlte Zinsen" value={fmtEUR(totalPaid)} valueClass="text-sky-700" sub="Gesamt" testid="zinsen-paid" />
          </dl>
        </DataCard>

        <DataCard title="Zuletzt aufgelaufene Zinsen" testid="card-recent-accruals" bgIcon={BgArt.chart} bgIconColor="#15803d">
          {recentAccruals.length === 0 ? (
            <p className="text-xs text-gray-500">Noch keine Zinsen verbucht.</p>
          ) : (
            <ul className="divide-y divide-gray-50 flex-1">
              {recentAccruals.map((a) => (
                <li key={a.id} className="grid grid-cols-[1fr_auto] gap-3 items-center py-3 text-sm" data-testid={`overview-accrual-${a.id}`}>
                  <span className="text-gray-700 tabular-nums">{formatDateUtc(a.period_start)} – {formatDateUtc(a.period_end)}</span>
                  <span className="font-medium text-emerald-700 tabular-nums text-right">+{fmtEUR(a.interest_amount_eur)}</span>
                </li>
              ))}
            </ul>
          )}
        </DataCard>

        <DataCard title="Schnellzugriff" testid="card-quick-access" bgIcon={BgArt.compass} bgIconColor="#C5A059">
          <div className="grid grid-cols-2 gap-3 flex-1 content-start">
            <QuickAction icon={Icon.doc}  label="Dokumente"     sub="Verträge & Unterlagen" onClick={() => setActiveTab('documents')}      testid="quick-documents" />
            <QuickAction icon={Icon.mail} label="Postfach"      sub="Nachrichten"           onClick={() => setActiveTab('messages')}       testid="quick-messages" />
            <QuickAction icon={Icon.bank} label="Bankverbindung" sub="Verwalten & prüfen"   onClick={() => setActiveTab('bank')}           testid="quick-bank" />
            <QuickAction icon={Icons.shield} label="Vollmachten" sub="Zugänge & Rechte"     onClick={() => setActiveTab('authorizations')} testid="quick-authorizations" />
          </div>
        </DataCard>
      </RevealStagger>
    </div>
  );
};

// ============================================================
// Single Investment card (used when only 1 investment)
// ============================================================
const InvestmentCard = ({ inv, onCsv, onPdf, onSaved }) => {
  const start = inv?.interest_start_date ? new Date(inv.interest_start_date) : null;
  const totalMonths = inv?.duration_months || 0;
  const elapsed = start ? Math.max(0, monthsBetween(start, new Date())) : 0;
  const cappedEl = totalMonths ? Math.min(elapsed, totalMonths) : elapsed;
  const pct = totalMonths ? Math.min(100, (cappedEl / totalMonths) * 100) : 0;

  return (
    <DataCard
      title={<EditableNickname investment={inv} defaultName={`${inv?.product_code || 'Investment'} · Vertrag #1`} onSaved={onSaved} />}
      action={
        <div className="flex items-center gap-2">
          <StatusChip tone="active"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>Aktiv</StatusChip>
          <ActionButton variant="secondary" size="xs" icon={Icon.download} onClick={onCsv} data-testid="export-csv-button">CSV</ActionButton>
          <ActionButton variant="secondary" size="xs" icon={Icon.download} onClick={onPdf} data-testid="export-pdf-button">PDF</ActionButton>
        </div>
      }
      testid="investment-card"
    >
      <span className="hidden" data-testid="investment-status">Aktiv</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
        <Field label="Einlage"        value={fmtEUR(inv?.principal_eur || 0)} mono />
        <Field label="Produkt"        value={inv?.product_code || '—'} />
        <Field label="Rendite"        value={fmtPct(inv?.apr || 0)} mono />
        <Field label="Auszahlung"     value="Monatlich" />
        <Field label="Vertragsbeginn" value={formatDateUtc(inv?.interest_start_date)} mono />
        <Field label="Vertragsende"   value={inv?.interest_end_date ? formatDateUtc(inv.interest_end_date) : 'Unbefristet'} mono />
      </div>

      <div className="mt-7 pt-6 border-t border-gray-100">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-[11px] text-gray-500 mb-1">Laufzeitfortschritt</p>
            <p className="font-heading text-3xl text-[#C5A059] tabular-nums leading-none">{pct.toFixed(0)} %</p>
          </div>
          <p className="text-xs text-gray-500 tabular-nums">
            {cappedEl} / {totalMonths || '∞'} Monate
          </p>
        </div>
        <div className="h-2 rounded-full bg-[#F4ECD8] overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#C5A059] to-[#9C7F3F] transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-5">
          <Sparkline seed={inv?.id || 1} />
        </div>
      </div>
    </DataCard>
  );
};

// ============================================================
// Compact row used when there are multiple investments
// ============================================================
const InvestmentRow = ({ inv, index, onSaved }) => {
  const start = inv?.interest_start_date ? new Date(inv.interest_start_date) : null;
  const totalMonths = inv?.duration_months || 0;
  const elapsed = start ? Math.max(0, monthsBetween(start, new Date())) : 0;
  const cappedEl = totalMonths ? Math.min(elapsed, totalMonths) : elapsed;
  const pct = totalMonths ? Math.min(100, (cappedEl / totalMonths) * 100) : 0;

  return (
    <div className="group relative grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr_1.4fr] gap-3 sm:gap-6 items-center p-4 sm:p-5 rounded-xl border border-gray-100 hover:border-[#C5A059]/50 hover:bg-[#FAF8F2]/50 hover:shadow-[0_8px_24px_-12px_rgba(197,160,89,0.25)] transition-all" data-testid={`investment-row-${inv.id}`}>
      {/* Subtle gold left accent on hover */}
      <div className="absolute left-0 top-2 bottom-2 w-0.5 bg-[#C5A059] rounded-r opacity-0 group-hover:opacity-100 transition-opacity"></div>

      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-[#C5A059] font-medium tabular-nums">#{index}</span>
          <EditableNickname investment={inv} defaultName={inv.product_code} size="sm" onSaved={onSaved} />
          <StatusChip tone="active" className="hidden sm:inline-flex">Aktiv</StatusChip>
        </div>
        <p className="text-[11px] text-gray-500 mt-1 tabular-nums">
          Beginn {formatDateUtc(inv.interest_start_date)}
          {totalMonths ? ` · ${totalMonths} Monate` : ' · Unbefristet'}
        </p>
      </div>
      <div className="sm:text-right">
        <p className="text-[10px] uppercase tracking-wider text-gray-400">Einlage</p>
        <p className="font-heading text-lg tabular-nums text-[#0A0A0A] leading-tight">{fmtEUR(inv.principal_eur)}</p>
      </div>
      <div className="sm:text-right">
        <p className="text-[10px] uppercase tracking-wider text-gray-400">Rendite</p>
        <p className="font-heading text-lg tabular-nums text-[#C5A059] leading-tight">{fmtPct(inv.apr)}</p>
      </div>
      <div>
        <div className="flex justify-between text-[11px] text-gray-500 mb-1.5 tabular-nums">
          <span className="uppercase tracking-wider text-gray-400 text-[10px]">Fortschritt</span>
          <span className="font-medium">{pct.toFixed(0)} % · {cappedEl}/{totalMonths || '∞'} M</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#F4ECD8] overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#9C7F3F] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Helpers
// ============================================================
const Field = ({ label, value, mono = false }) => (
  <div>
    <p className="text-[11px] text-gray-500 mb-1">{label}</p>
    <p className={`text-[#0A0A0A] font-medium ${mono ? 'tabular-nums' : ''}`}>{value}</p>
  </div>
);

const DataLine = ({ label, value, sub, valueClass = 'text-[#0A0A0A]', testid }) => (
  <div data-testid={testid}>
    <p className="text-[11px] text-gray-500">{label}</p>
    <p className={`font-heading text-2xl ${valueClass} tabular-nums mt-1 leading-none`}>{value}</p>
    {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
  </div>
);

const QuickAction = ({ icon, label, sub, onClick, testid }) => (
  <button
    onClick={onClick}
    className="text-left p-3 rounded-xl border border-gray-100 hover:border-[#C5A059]/40 hover:bg-[#FAF8F2] transition flex items-start gap-3 group"
    data-testid={testid}
  >
    <span className="w-9 h-9 rounded-lg bg-[#F4ECD8] flex items-center justify-center flex-shrink-0 text-[#C5A059] group-hover:bg-[#EAD8AA] transition-colors">{icon}</span>
    <div className="min-w-0">
      <p className="text-sm font-medium text-[#0A0A0A] truncate">{label}</p>
      <p className="text-[11px] text-gray-500 truncate">{sub}</p>
    </div>
  </button>
);

// ============================================================
// Beraterstreifen — premium personal-advisor banner
// ============================================================
const AdvisorStrip = ({ advisor }) => {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const photoSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return `${BACKEND}${url}`;
    return url;
  };
  const name = advisor?.name || 'Felix Pfisterer';
  const role = advisor?.role || 'Persönlicher Berater';
  const phone = advisor?.phone || '+49 711 925 354 91';
  const email = advisor?.email || 'pfisterer@valueplus.investments';
  const hours = advisor?.hours || '10:00 – 18:00 Uhr';
  const photo = photoSrc(advisor?.photo_url);
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('');

  return (
    <section
      className="relative rounded-2xl overflow-hidden p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 ring-1 ring-[#1F1F1F]"
      style={{ background: 'radial-gradient(circle at 0% 100%, #1F1F1F 0%, #0A0A0A 60%)' }}
      data-testid="advisor-strip"
    >
      {/* Subtle gold pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{ backgroundImage: 'radial-gradient(#C5A059 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#C5A059]/15 blur-3xl pointer-events-none" />

      <div className="relative flex items-center gap-4 flex-1 min-w-0">
        <div className="relative">
          {photo ? (
            <img src={photo} alt={name} className="w-14 h-14 rounded-full object-cover ring-2 ring-[#C5A059]/30" />
          ) : (
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-[#0A0A0A] font-heading text-lg bg-gradient-to-br from-[#E8D9A8] to-[#C5A059] ring-2 ring-[#C5A059]/30">
              {initials}
            </div>
          )}
          <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-[#0A0A0A]"></span>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-1">{role}</p>
          <p className="font-heading text-base sm:text-lg text-white leading-tight" data-testid="advisor-name">{name}</p>
          <p className="text-xs text-white/60 mt-0.5">Für Sie persönlich erreichbar · {hours}</p>
        </div>
      </div>

      <div className="relative flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
        <a
          href={`tel:${phone.replace(/\s/g, '')}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition border border-white/10 text-white text-xs sm:text-sm tabular-nums"
          data-testid="advisor-call"
        >
          <svg className="w-4 h-4 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.28a1 1 0 01.95.68l1.5 4.49a1 1 0 01-.5 1.21l-2.1 1.05a11 11 0 005.5 5.5l1.05-2.1a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.7 21 3 14.3 3 6V5z"/></svg>
          {phone}
        </a>
        <a
          href={`mailto:${email}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C5A059] hover:bg-[#B08E48] transition text-[#0A0A0A] text-xs sm:text-sm font-medium"
          data-testid="advisor-mail"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
          E-Mail
        </a>
      </div>
    </section>
  );
};

// ============================================================
// EditableNickname — inline rename for the customer's own investment
// ============================================================
const EditableNickname = ({ investment, defaultName, onSaved, size = 'md' }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(investment?.nickname || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(investment?.nickname || ''); }, [investment?.nickname]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = async () => {
    const next = value.trim().slice(0, 60);
    if ((next || '') === (investment?.nickname || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/customer/investments/${investment.id}/nickname`, { nickname: next });
      onSaved?.(next || null);
      toast.success(next ? 'Bezeichnung gespeichert' : 'Bezeichnung entfernt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const displayed = investment?.nickname || defaultName || 'Investment';
  const textClass = size === 'sm'
    ? 'font-medium text-[#0A0A0A] truncate'
    : 'font-heading text-lg sm:text-xl text-[#0A0A0A] leading-tight';

  if (editing) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, 60))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
          maxLength={60}
          placeholder={defaultName || 'Bezeichnung'}
          disabled={saving}
          className="flex-1 min-w-0 px-2 py-1 text-sm border border-[#C5A059]/50 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#C5A059]/30"
          data-testid={`investment-nickname-input-${investment.id}`}
        />
        <button onClick={save} disabled={saving} className="text-xs px-2 py-1 rounded bg-[#0A0A0A] text-white hover:bg-[#1F1F1F] vp-tactile" data-testid={`investment-nickname-save-${investment.id}`}>
          {saving ? '…' : 'OK'}
        </button>
        <button onClick={() => setEditing(false)} disabled={saving} className="text-xs px-2 py-1 rounded bg-secondary hover:bg-gray-200 vp-tactile">
          Abbrechen
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-2 min-w-0 max-w-full text-left"
      title="Bezeichnung bearbeiten"
      data-testid={`investment-nickname-edit-${investment.id}`}
    >
      <span className={textClass}>{displayed}</span>
      <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-300 group-hover:text-[#C5A059] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5h-2a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M18.5 4.5a2.121 2.121 0 113 3L13 16l-4 1 1-4 8.5-8.5z" />
      </svg>
    </button>
  );
};

export default PremiumOverview;
